import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Scissors, Leaf, Droplets, BadgePercent, Users, CreditCard } from 'lucide-react';
import { services, galleryImages } from '../data/mockData';
import { SiteVideo } from '../components/SiteVideo';

const Entretien = () => {
  const entretienImages = galleryImages.filter(img => img.category === 'entretien');
  
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-green-50 via-white to-green-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte à gauche */}
            <div>
              <div className="inline-block mb-6 px-4 py-2 bg-green-100 rounded-full">
                <span className="text-green-700 font-semibold text-sm">Service</span>
              </div>
              <h1 data-testid="entretien-page-title" className="text-4xl md:text-6xl font-bold mb-6 text-green-700">
                {services.entretien.title}
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-6">
                Un jardin bien entretenu est un jardin qui reste beau toute l'année. Nous proposons des contrats d'entretien à l'année ou des interventions ponctuelles, adaptés à vos besoins et à votre budget.
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                Notre équipe intervient régulièrement pour maintenir vos espaces verts en parfait état. De la tonte de pelouse à la taille de haies, en passant par l'entretien des massifs, nous prenons soin de votre jardin en toutes saisons.
              </p>
            </div>

            {/* Vidéo à droite */}
            <div className="flex items-center justify-center">
              <SiteVideo name="entretien" label="Entretien paysager Perinature" />
            </div>
          </div>
        </div>
      </section>

      {/* Avantages Fiscaux Section - Highlighted */}
      <section className="py-16 px-4 bg-gradient-to-br from-green-600 to-green-700">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center gap-4 mb-6">
                <div className="inline-block p-4 bg-green-100 rounded-full">
                  <BadgePercent className="text-green-600" size={48} />
                </div>
                <div className="inline-block p-4 bg-green-100 rounded-full">
                  <Users className="text-green-600" size={48} />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {services.entretien.fiscalAdvantage.title}
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                {services.entretien.fiscalAdvantage.description}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {services.entretien.fiscalAdvantage.benefits.map((benefit, index) => (
                <div
                  key={index}
                  data-testid={`entretien-fiscal-benefit-${index}`}
                  className="p-6 bg-gradient-to-br from-green-50 to-white rounded-xl border-2 border-green-100 hover:border-green-300 transition-all duration-300"
                >
                  <div className="flex items-center justify-center mb-4">
                    <CreditCard className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 text-center">{benefit.title}</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            Nos prestations d'entretien
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.entretien.features.map((feature, index) => (
              <div
                key={index}
                data-testid={`entretien-feature-${index}`}
                className="flex items-start gap-4 p-6 bg-white rounded-xl border-2 border-green-100 hover:border-green-300 transition-all duration-300 hover:shadow-lg"
              >
                <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={24} />
                <p className="text-gray-700 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Types */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
            Nos spécialités
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scissors className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Taille</h3>
              <p className="text-gray-600">
                Taille précise de haies, arbustes et arbres pour maintenir l'esthétique de votre jardin.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Entretien des massifs</h3>
              <p className="text-gray-600">
                Désherbage, paillage et soin des plantes pour des massifs toujours impeccables.
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Droplets className="text-white" size={36} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Pelouse</h3>
              <p className="text-gray-600">
                Tonte régulière, scarification et fertilisation pour une pelouse verdoyante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {entretienImages.length > 0 && (
        <section className="py-16 px-4 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center text-gray-900">
              Exemples de nos interventions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {entretienImages.map((image) => (
                <div
                  key={image.id}
                  data-testid={`entretien-gallery-item-${image.id}`}
                  className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                >
                  <img
                    src={image.url}
                    data-testid={`entretien-gallery-image-${image.id}`}
                    alt={image.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <h3 className="font-bold text-lg">{image.title}</h3>
                      <p className="text-sm text-gray-200">{image.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-br from-green-600 to-green-700">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">
            Profitez de 50% de crédit d'impôt
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Grâce à notre partenariat avec une coopérative, l'entretien de votre jardin devient encore plus accessible.
          </p>
          <Link
            to="/contact"
            data-testid="entretien-contact-link"
            className="inline-block px-8 py-4 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 transition-all duration-200 hover:scale-105 shadow-xl"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Entretien;
